import { createClient } from "@/utils/supabase/client";
import { avaApiUrl, emaApiUrl, nowadaysApiUrl, zedApiUrl } from "@/utils/url-utils";

// One-time cleanup of the legacy JWT cache from before this module moved to cookie-only storage.
if (typeof window !== "undefined") {
  localStorage.removeItem("supabase.auth.token");
}

// `createBrowserClient` from @supabase/ssr is internally a singleton per (url, key),
// so calling `createClient()` on every request is cheap — no userland caching needed.
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Enum for different API servers
 */
export enum ApiServer {
  BACKEND = "backend",
  AVA = "ava",
  EMA = "ema",
  ZED = "zed",
}

/**
 * Gets the appropriate API URL based on the server type
 */
function getApiUrl(server: ApiServer, path: string): string {
  switch (server) {
    case ApiServer.BACKEND:
      return nowadaysApiUrl(path);
    case ApiServer.AVA:
      return avaApiUrl(path);
    case ApiServer.EMA:
      return emaApiUrl(path);
    case ApiServer.ZED:
      return zedApiUrl(path);
    default:
      return nowadaysApiUrl(path);
  }
}

/**
 * Refreshes the Supabase auth token via the browser client.
 * Concurrent callers share a single in-flight refresh to avoid redundant round-trips
 * when many requests 401 at once (e.g. on tab wake). The token is not persisted in
 * localStorage; the Supabase session cookie is the source of truth.
 * @returns The new access token or null if refresh failed
 */
async function refreshAuthToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase.auth.refreshSession();

      if (error || !data.session?.access_token) {
        console.error("Failed to refresh token:", error);
        return null;
      }

      return data.session.access_token;
    } catch (error) {
      console.error("Error refreshing token:", error);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Gets the current auth token from the Supabase session (cookie-backed).
 * @returns The current access token or null if not available
 */
async function getCurrentToken(): Promise<string | null> {
  try {
    const supabase = createClient();
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch (error) {
    console.error("Error getting current token:", error);
    return null;
  }
}

type DataTransmissionType = "json" | "form";

/**
 * Helper function to prepare headers with auth token and appropriate Content-Type
 */
function prepareHeaders(token: string, dataType: DataTransmissionType, customHeaders?: HeadersInit): HeadersInit {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  // Set Content-Type based on data transmission type
  if (dataType === "json") {
    headers["Content-Type"] = "application/json";
  }
  // For "form" type, don't set Content-Type - let browser set it with boundary

  return {
    ...headers,
    ...(customHeaders as Record<string, string>),
  };
}

/**
 * Credentials mode for a given API server. ZED reads the cross-subdomain
 * `nw_active_org` cookie to scope requests to the active org, so its requests
 * must send credentials; other backends keep the default (`same-origin`) to
 * avoid breaking their CORS (a wildcard `Access-Control-Allow-Origin` is
 * incompatible with credentialed requests).
 */
function credentialsFor(server: ApiServer): RequestCredentials | undefined {
  return server === ApiServer.ZED ? "include" : undefined;
}

/**
 * Helper function to make a fetch request with prepared headers
 */
async function makeRequest(url: string, options: RequestInit, headers: HeadersInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Makes an authenticated request to the specified API server with automatic token refresh on 401 errors.
 * @param path The API path (passed to the appropriate API URL helper based on `server`)
 * @param options Fetch options (method, body, headers, etc.)
 * @param dataType The type of data being transmitted ("json" or "form")
 * @param server The API server to use (defaults to BACKEND)
 * @returns Promise resolving to the fetch Response
 * @throws {Error} If no auth token is available, or if a 401 retry fails to refresh the token.
 */
export async function fetchWithAuth(
  path: string,
  options: RequestInit = {},
  dataType: DataTransmissionType = "json",
  server: ApiServer = ApiServer.BACKEND
): Promise<Response> {
  const url = getApiUrl(server, path);

  // Get current token
  let token = await getCurrentToken();

  if (!token) {
    throw new Error("No authentication token available");
  }

  // Send the active-org cookie to ZED (cross-subdomain); other servers keep the
  // `same-origin` default (credentialsFor returns undefined, which fetch ignores).
  const requestOptions: RequestInit = { ...options, credentials: credentialsFor(server) };

  // Prepare headers and make initial request
  const headers = prepareHeaders(token, dataType, options.headers);
  let response = await makeRequest(url, requestOptions, headers);

  // If we get a 401, try to refresh the token and retry once
  if (response.status === 401) {
    const newToken = await refreshAuthToken();

    if (!newToken) {
      // If token refresh failed, redirect to login
      console.error("Token refresh failed, redirecting to login");
      window.location.href = "/login";
      throw new Error("Authentication failed and token refresh unsuccessful");
    }

    // Prepare new headers and retry request
    const newHeaders = prepareHeaders(newToken, dataType, options.headers);
    response = await makeRequest(url, requestOptions, newHeaders);
  }

  return response;
}

/**
 * Makes a GET request to the specified API server
 * @param path The API path (will be passed to the appropriate API URL helper)
 * @param server The API server to use (defaults to BACKEND)
 * @param options Optional fetch options (headers, etc.)
 * @returns Promise resolving to the fetch Response
 */
export async function fetchGET(
  path: string,
  server: ApiServer = ApiServer.BACKEND,
  options?: RequestInit
): Promise<Response> {
  const url = getApiUrl(server, path);
  return fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    credentials: credentialsFor(server),
    ...options,
  });
}

/**
 * Makes an authenticated GET request to the specified API server
 * @param path The API path
 * @param server The API server to use (defaults to BACKEND)
 * @returns Promise resolving to the fetch Response
 */
export async function fetchGETWithAuth(path: string, server: ApiServer = ApiServer.BACKEND): Promise<Response> {
  return fetchWithAuth(path, { method: "GET" }, "json", server);
}

/**
 * Makes an authenticated POST request to the specified API server
 * @param path The API path
 * @param body The request body (will be JSON stringified)
 * @param customHeaders Optional additional headers
 * @param server The API server to use (defaults to BACKEND)
 * @param signal Optional AbortSignal — useful for streaming responses where the
 *   caller may need to abort before headers return.
 * @returns Promise resolving to the fetch Response
 */
export async function fetchPOSTWithAuth(
  path: string,
  body?: unknown,
  customHeaders: HeadersInit = {},
  server: ApiServer = ApiServer.BACKEND,
  signal?: AbortSignal
): Promise<Response> {
  return fetchWithAuth(
    path,
    {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
      headers: customHeaders,
      signal,
    },
    "json",
    server
  );
}

/**
 * Makes an authenticated POST request to the specified API server with FormData
 * @param path The API path
 * @param formData The FormData to send
 * @param customHeaders Optional custom headers (Content-Type will be omitted for FormData)
 * @param server The API server to use (defaults to BACKEND)
 * @returns Promise resolving to the fetch Response
 */
export async function fetchFormPOSTWithAuth(
  path: string,
  formData: FormData,
  customHeaders: HeadersInit = {},
  server: ApiServer = ApiServer.BACKEND
): Promise<Response> {
  return fetchWithAuth(
    path,
    {
      method: "POST",
      body: formData,
      headers: customHeaders,
    },
    "form",
    server
  );
}

/**
 * Makes an authenticated PATCH request to the specified API server
 * @param path The API path
 * @param body The request body (will be JSON stringified)
 * @param customHeaders Optional additional headers
 * @param server The API server to use (defaults to BACKEND)
 * @returns Promise resolving to the fetch Response
 */
export async function fetchPATCHWithAuth(
  path: string,
  body?: unknown,
  customHeaders: HeadersInit = {},
  server: ApiServer = ApiServer.BACKEND
): Promise<Response> {
  return fetchWithAuth(
    path,
    {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
      headers: customHeaders,
    },
    "json",
    server
  );
}

/**
 * Makes an authenticated DELETE request to the specified API server
 * @param path The API path
 * @param customHeaders Optional additional headers
 * @param server The API server to use (defaults to BACKEND)
 * @returns Promise resolving to the fetch Response
 */
export async function fetchDELETEWithAuth(
  path: string,
  customHeaders: HeadersInit = {},
  server: ApiServer = ApiServer.BACKEND
): Promise<Response> {
  return fetchWithAuth(
    path,
    {
      method: "DELETE",
      headers: customHeaders,
    },
    "json",
    server
  );
}

/**
 * Makes an authenticated SSE (Server-Sent Events) request to the specified API server
 * @param path The API path (can include query parameters)
 * @param signal Optional AbortSignal for request cancellation
 * @param server The API server to use (defaults to BACKEND)
 * @returns Promise resolving to the fetch Response
 */
export async function fetchSSEWithAuth(
  path: string,
  signal?: AbortSignal,
  server: ApiServer = ApiServer.BACKEND
): Promise<Response> {
  return fetchWithAuth(
    path,
    {
      method: "GET",
      signal,
    },
    "json",
    server
  );
}

/**
 * Makes an authenticated SSE POST request with FormData to the specified API server.
 * This always refreshes the token first since FormData can only be sent once (no retry possible).
 * @param path The API path
 * @param formData The FormData to send
 * @param signal Optional AbortSignal for request cancellation
 * @param server The API server to use (defaults to BACKEND)
 * @returns Promise resolving to the fetch Response
 * @throws {Error} If the token refresh yields no auth token.
 */
export async function fetchSSEFormPOSTWithAuth(
  path: string,
  formData: FormData,
  signal?: AbortSignal,
  server: ApiServer = ApiServer.BACKEND
): Promise<Response> {
  const url = getApiUrl(server, path);

  // Always refresh token first since FormData can't be re-sent on 401 retry
  const token = await refreshAuthToken();

  if (!token) {
    throw new Error("No authentication token available");
  }

  return fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
    credentials: credentialsFor(server),
    signal,
  });
}
